// src/components/promotion/PromotionTable.tsx
import { Table, Tag, Typography, Space, Empty, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import CustomButton from "src/components/common/CustomButton";
import type {
  PromotionListItem,
  PromotionType,
  PromotionStatus,
} from "src/api/promotion/promotionApi";

type Props = {
  list: PromotionListItem[];
  onViewDetail: (item: PromotionListItem) => void;
  onEdit: (item: PromotionListItem) => void;
  onDelete: (item: PromotionListItem) => void;
  onStatusChange: (item: PromotionListItem, newStatus: PromotionStatus) => void;
  disabled?: boolean;
};

const PromotionTypeLabel: Record<PromotionType, string> = {
  EVENT: "이벤트",
  TIME_DEAL: "타임딜",
  FLASH_SALE: "플래시세일",
  SEASONAL: "시즌특가",
};

export default function PromotionTable({ list, onViewDetail, onEdit, onDelete, onStatusChange, disabled }: Props) {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(
      2,
      "0"
    )}`;
  };

  const columns: ColumnsType<PromotionListItem> = [
    { title: "ID", dataIndex: "promotionId", key: "promotionId", width: 80 },
    {
      title: "프로모션코드",
      dataIndex: "promotionCode",
      key: "promotionCode",
      width: 130,
      render: (value: string) => (
        <Typography.Text style={{ fontFamily: "monospace", fontSize: 13 }}>{value}</Typography.Text>
      ),
    },
    {
      title: "프로모션명",
      dataIndex: "promotionName",
      key: "promotionName",
      width: 200,
      ellipsis: true,
      render: (value: string) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: "타입",
      dataIndex: "promotionType",
      key: "promotionType",
      width: 100,
      align: "center",
      render: (value: PromotionType) => <Tag>{PromotionTypeLabel[value]}</Tag>,
    },
    {
      title: "상태",
      dataIndex: "promotionStatus",
      key: "promotionStatus",
      width: 140,
      align: "center",
      render: (value: PromotionStatus, record: PromotionListItem) => {
        // 현재 상태에 따라 변경 가능한 옵션 설정
        const getOptions = () => {
          if (value === "DRAFT") {
            return [
              { label: "임시저장", value: "DRAFT" },
              { label: "활성화", value: "ACTIVE" },
            ];
          } else if (value === "ACTIVE") {
            return [
              { label: "활성화", value: "ACTIVE" },
              { label: "취소", value: "CANCELED" },
            ];
          } else {
            return [{ label: "취소", value: "CANCELED" }];
          }
        };

        return (
          <Select<PromotionStatus>
            value={value}
            onChange={(newStatus) => onStatusChange(record, newStatus)}
            options={getOptions()}
            style={{ width: 120 }}
            size="small"
            disabled={disabled || value === "CANCELED"}
          />
        );
      },
    },
    {
      title: "시작일시",
      dataIndex: "startDate",
      key: "startDate",
      width: 150,
      render: (value: string) => (
        <Typography.Text style={{ fontSize: 12 }}>{formatDate(value)}</Typography.Text>
      ),
    },
    {
      title: "종료일시",
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: (value: string) => (
        <Typography.Text style={{ fontSize: 12 }}>{formatDate(value)}</Typography.Text>
      ),
    },
    {
      title: "상품수",
      dataIndex: "productCount",
      key: "productCount",
      width: 80,
      align: "center",
    },
    {
      title: "관리",
      key: "actions",
      width: 200,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <CustomButton onClick={() => onEdit(record)} disabled={disabled}>
            수정
          </CustomButton>
          <CustomButton customVariant="danger" onClick={() => onDelete(record)} disabled={disabled}>
            삭제
          </CustomButton>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ border: "1px solid #e0e0e0", borderRadius: 8 }}>
      <Table
        tableLayout="fixed"
        columns={columns}
        dataSource={list}
        pagination={false}
        rowKey="promotionId"
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="데이터가 없습니다." /> }}
      />
    </div>
  );
}
